import { getCountyLabel } from './countyLookup';

// Per-lead status for the underwriting outputs: ARV / Repairs / Profit.
// Pure derivation from lead fields — no side effects. Used by the New Deals
// activity feed to show one dot per calc with a hover-explained reason.
//
// Field paths (verified against propertyIngestionV2 writers):
//   data.underwriting.arv             — number or null
//   data.underwriting.repairCost      — number or null
//   data.underwriting.estimatedProfit — number or null
//   data.underwriting.status          — 'complete' | 'skipped' | 'failed' | 'partial'
//   data.underwriting.reason          — eligibility skip code (subset stored)
//   data.underwriting.details         — { propertyType?, countyId?, beds?, baths?, sqft? }
//
// Several skip reasons aren't persisted to the doc — we infer them from
// other lead fields (propertyType, address, verifiedCharacteristics,
// imageUrls/imageFolders) so the tooltip never has to fall back to a vague
// "Not calculated" when the data is there to do better.

export const CALC_STATES = {
    OK:        'ok',
    ESTIMATED: 'estimated',
    SKIPPED:   'skipped',
    PENDING:   'pending'
};

// ---------------------------------------------------------------------------
// Persisted underwriting.reason → label
// ---------------------------------------------------------------------------
const UW_REASON_LABELS = {
    no_property_id:           'Address could not be matched to a property',
    invalid_property_type:    'Property type not supported',
    unsupported_county:       'County is not in supported markets',
    missing_characteristics:  'Missing beds, baths or sqft',
    incomplete_address:       'Incomplete address',
    address_unverified:       'Address could not be verified (no house number)',
    underwriting_disabled:    'Underwriting is turned off for this lead',
    insufficient_credits:     'Not enough credits — top up to analyze this deal'
};

// data.underwriting.arvSkipReason — written at arvClient.js exit paths
// (future ingests only). Maps the raw error codes to friendly labels.
const ARV_REASON_LABELS = {
    synthetic_property_id:           'Address could not be matched to a real property record',
    no_county_id:                    'County could not be resolved for this property',
    no_comps:                        'No comparable sales found in this market',
    aws_fetch_failed:                'Property data couldn\'t be fetched (AWS lookup failed) — usually transient',
    // May-2026 compFetcherV1 cutover — these surface when V1 is unavailable
    // or can\'t build a subject for the lead. Codes match the strings written
    // by processor.js / arvClient.js so the exact-match lookup catches them.
    compfetcher_subject_unbuildable: 'Property doesn\'t have a Zillow record we can use — ARV needs one',
    comp_fetcher_progressive_failed: 'Property data couldn\'t be fetched (comp service failed) — usually transient',
    comp_fetcher_failed:             'Property data couldn\'t be fetched (comp service failed) — usually transient',
    // Per-company analysis toggle off (data.features.comps = false) — deliberate, not a failure.
    comps_disabled:                  'Comps / ARV analysis is turned off for this account',
    // Comps pool-only mode (data.features.compsPoolOnly = true) — also deliberate. The comps
    // ARE there to look at; we just didn't value them. Without this entry the lookup falls
    // through to inferHardBlockReason, which would guess at a blocker that doesn't exist.
    analysis_disabled:               'Comps were pulled but not analysed — this account values them manually',
    // Co-living is this account's only strategy and a pre-analysis gate ruled the property out
    // (not single-family / flood zone / HOA / under contract), so nothing paid was run — see
    // processor.js's coLivingOnlyDeadLead. Deliberate, and NOT a failure or a missing capability:
    // without this entry the lookup falls through to inferHardBlockReason, which would invent a
    // blocker (usually "not eligible") for a house that was perfectly eligible.
    co_living_gated:                 'Ruled out for co-living, which is this account\'s only strategy — no further analysis was run',
};

// data.underwriting.repairSkipReason — written at processor.js exit paths
// (future ingests only). Covers the cases where eligibility passed but the
// repair-cost pipeline itself didn't produce a number.
const REPAIR_REASON_LABELS = {
    no_square_footage:    'Missing square footage (repair estimate needs sqft)',
    no_images:            'No photos available — repairs aren\'t estimated without photos',
    google_photos_only:   'Only Google Photos links were provided — that source can\'t be processed yet',
    cloud_folder_failed:  'Images were in cloud folders we couldn\'t access (Drive / Dropbox / OneDrive)',
    template_lookup:      'Repair-cost template lookup failed',
    vision_api_failed:    'Vision API failed analyzing the photos',
    // Per-company analysis toggle off (data.features.repairs = false) — deliberate, not a failure.
    repairs_disabled:     'Repair-cost analysis is turned off for this account',
    // Vacant land — no structure exists, so there's nothing to estimate repairs on.
    vacant_land:          'Vacant land — no structure to estimate repairs on',
    // Twin of the ARV entry above — the photos were never read because co-living (this
    // account's only strategy) had already ruled the property out.
    co_living_gated:      'Ruled out for co-living, which is this account\'s only strategy — the photos weren\'t analysed',
};

// Property types the underwriting pipeline supports. Matches the allow-list
// in functions/propertyIngestionV2/underwriting/eligibility.js — anything
// not on this list is rejected as "invalid_property_type".
const SUPPORTED_PROPERTY_TYPES = new Set([
    'single family', 'sfr', 'single_family',
    'single family residence', 'residential'
]);

const isNumber = (v) => v != null && !isNaN(v) && typeof v === 'number';

// ---------------------------------------------------------------------------
// processingSteps (the ingestion storyboard) — the FACTUAL record of why
// underwriting was skipped/failed, written per-lead by propertyIngestionV2.
// Prefer it over the heuristic inference below; the heuristics remain the
// fallback for legacy leads ingested before the storyboard existed.
// ---------------------------------------------------------------------------
function stepUnderwriting(data) {
    return data?.processingSteps?.underwriting || null;
}

// True when the underwriting step is still in flight. The top-level lead
// status is written 'complete' at lead creation (batchedFirestore), before
// ARV is computed asynchronously — so it can't distinguish "still running"
// from "finished". The storyboard step can: status 'running'/'pending', or a
// startedAt with no completedAt, means the calc hasn't landed yet.
function stepStillRunning(step) {
    if (!step) return false;
    return step.status === 'running'
        || step.status === 'pending'
        || (!!step.startedAt && !step.completedAt);
}

// Maps a skipped/failed underwriting step to a { state, reason }. Returns null
// when the step didn't block (absent, running, or completed) so the caller
// falls through to value-present / heuristic handling.
function stepBlockReason(step) {
    if (!step) return null;
    if (step.status === 'skipped') {
        const label = UW_REASON_LABELS[step.reason] || step.reason || 'Underwriting was skipped';
        return { state: CALC_STATES.SKIPPED, reason: label };
    }
    if (step.status === 'failed') {
        return {
            state: CALC_STATES.SKIPPED,
            reason: step.rateLimited
                ? 'Underwriting was rate-limited — it will retry automatically.'
                : 'Underwriting failed while running.'
        };
    }
    return null;
}

function describeWithDetail(label, details) {
    if (!details) return label;
    if (details.propertyType) return `${label}: ${details.propertyType}`;
    if (details.countyId) {
        const countyLabel = getCountyLabel(details.countyId);
        return `${label} (${countyLabel})`;
    }
    if (details.beds != null || details.baths != null || details.sqft != null) {
        const missing = [];
        if (!details.beds) missing.push('beds');
        if (!details.baths) missing.push('baths');
        if (!details.sqft) missing.push('sqft');
        if (missing.length > 0) return `${label}: ${missing.join(', ')}`;
    }
    return label;
}

// ---------------------------------------------------------------------------
// Heuristic reason inference — for cases the pipeline didn't persist
// ---------------------------------------------------------------------------

// Reasons the underwriting pipeline would have hard-blocked on, derived from
// data on the lead. Order matters: returns the first applicable reason so
// the user sees the root cause, not a downstream symptom.
function inferHardBlockReason(data) {
    if (data.syntheticPropertyId === true) {
        return 'Address could not be matched to a real property record';
    }
    if (!data.address?.streetAddress || !data.address?.city) {
        return 'Incomplete address';
    }
    const pt = (data.propertyType || '').toLowerCase().trim();
    if (pt && !SUPPORTED_PROPERTY_TYPES.has(pt)) {
        const friendly = pt
            .replace(/_/g, ' ')
            .replace(/\b\w/g, (c) => c.toUpperCase());
        return `Property type not supported: ${friendly}`;
    }
    if (!pt) {
        return 'Property type not yet identified';
    }
    const chars = data.verifiedCharacteristics || {};
    const missing = [];
    if (!chars.bedrooms) missing.push('beds');
    if (!chars.bathrooms) missing.push('baths');
    if (!chars.livingSquareFeet) missing.push('sqft');
    if (missing.length > 0) {
        return `Missing ${missing.join(', ')}`;
    }
    return null;
}

// Best-effort inference for why repair cost might be null when the lead
// has no persisted skip reason. Looks at every image source on the lead
// doc — imageUrls, imageFolders, photoUrls, dealUrls — and reports the
// shape of what's there (or isn't). The pipeline's sqft-based fallback
// normally fires when nothing usable is around, so a null repairCost
// + zero usable images is the most likely human-readable cause for
// legacy leads written before repairSkipReason existed.
function inferRepairImageProblem(data) {
    const imageUrls = Array.isArray(data.imageUrls) ? data.imageUrls : [];
    const photos = data.photoUrls || {};
    const folders = data.imageFolders || {};
    const dealUrls = Array.isArray(data.dealUrls) ? data.dealUrls : [];

    let usable = 0;
    let googlePhotosCount = 0;
    for (const url of imageUrls) {
        if (typeof url !== 'string') continue;
        if (/photos\.google\.com|googleusercontent\.com|lh3\.googleusercontent/.test(url)) {
            googlePhotosCount++;
            continue;
        }
        usable++;
    }
    if (photos.zillow || photos.redfin || photos.realtor) usable++;

    const folderCount = (folders.drive?.length || 0)
        + (folders.dropbox?.length || 0)
        + (folders.oneDrive?.length || 0);

    if (usable > 0) return null; // had images — must be a different cause
    if (googlePhotosCount > 0 && folderCount === 0 && dealUrls.length === 0) {
        return 'Only Google Photos links were provided — that source can\'t be processed yet';
    }
    if (folderCount > 0 && imageUrls.length === 0) {
        return 'Images were in cloud folders we couldn\'t access (Drive / Dropbox / OneDrive)';
    }
    if (dealUrls.length > 0 && imageUrls.length === 0) {
        return 'Deal-page URLs were provided but no images could be extracted from them';
    }
    if (imageUrls.length === 0 && folderCount === 0 && dealUrls.length === 0) {
        return 'No images provided and the sqft-fallback didn\'t produce a number';
    }
    return null;
}

// Underwriting statuses that mean the analysis has SETTLED — it ran to a
// conclusion and there is nothing left in flight. 'pending_arv' is deliberately
// absent: it's the one status that means phase 2 still legitimately owes an ARV.
//   • 'partial' — repairs computed, ARV genuinely empty (no comps found)
//   • 'skipped' / 'failed' / 'skipped_co_living_only' — hard block at the gate
//   • 'complete' — fully done
const SETTLED_UNDERWRITING = [
    'complete', 'partial', 'skipped', 'failed', 'skipped_co_living_only'
];

// Timestamps reach the table as `{_seconds, _nanoseconds}` over the getOrderedDocs
// wire and as real Firestore Timestamps on the pages that read a doc directly.
// Tolerate both (plus Date/number/string) — a helper that understood only one form
// would silently return NaN on the other and every window test below would fail
// open, which is the failure mode this whole block exists to avoid.
const toMillis = (v) => {
    if (!v) return null;
    if (typeof v.toDate === 'function') return v.toDate().getTime();
    if (typeof v === 'object' && v._seconds !== undefined) {
        return v._seconds * 1000 + (v._nanoseconds || 0) / 1000000;
    }
    const ms = v instanceof Date ? v.getTime() : new Date(v).getTime();
    return Number.isFinite(ms) ? ms : null;
};

// How long we keep waiting for the co-living deal score before calling the lead
// finished without one.
//
// ⚠️ THE WINDOW IS THE WHOLE SAFETY ARGUMENT. The score arrives in underwriting
// PHASE 2 — the ARV agent, measured at ~6.5 min — which runs after `status` has
// already flipped to 'complete' and after `underwriting.status` has settled (for a
// co-living / comps-pool-only lead that settled value is 'skipped', which is
// transient and correct). So every "is it done" signal the row already trusts says
// YES while the number that decides the lead's rank is still missing.
//
// But a bridge deal can also legitimately never score — a comps-pool-only company
// whose pool has no usable comp produces no stabilized value, so the rank comes back
// blocked forever. Without a bound that lead would render as working, refuse to open
// and hold a live Firestore listener for the rest of the session. That is exactly the
// stranded-liveness-flag failure the comment above describes (it wedged a customer's
// lead for 5 days), so this waits on a CLOCK, not on a flag anyone has to clear.
const CO_LIVING_SCORE_WINDOW_MS = 45 * 60 * 1000;

/**
 * True while a lead's co-living deal score is still expected but hasn't landed.
 *
 * Keyed on `coLivingFinancingPath === 'bridgeRefi'` rather than on "co-living ran",
 * because that is the ONLY state where a score is owed. `cashOutRankFields` writes the
 * path at phase 1 alongside a null score, and a `dscr` house is scored by nothing and
 * never will be — waiting on it would hold every clean lead open forever.
 */
export function isCoLivingScorePending(lead) {
    if (!lead) return false;
    if (lead.coLivingFinancingPath !== 'bridgeRefi') return false;
    if (lead.coLivingDealScore != null) return false;
    const started = toMillis(lead.createdAt);
    return started != null && (Date.now() - started) < CO_LIVING_SCORE_WINDOW_MS;
}

// A lead is still "working" — mid-pipeline and NOT safe to open — when either:
//   - status is queued/processing (enrichment still running), OR
//   - status is complete but processingStage is still 'running_underwriting' /
//     'finalizing'. Underwriting phase 2 (the ARV agent) runs AFTER status flips
//     to 'complete' — saveLeadBatched sets complete before processUnderwriting
//     finishes — so top-level status alone doesn't mean the analysis is done.
//     processingStage is cleared once phase 2 lands (see batchedFirestore.js /
//     arvFinalizationWorker.js). Co-living runs in phase 1, so a co-living-only
//     lead is done as soon as status='complete' (stage already cleared).
//
// ...but both status and processingStage are LIVENESS flags, and liveness flags
// get stranded. If the worker crashes between settling the underwrite and clearing
// the stage, the lead keeps a stage forever: the row renders blue and refuses to
// open even though the analysis finished days ago. That stranded a customer's lead
// for 5 days — they could see the deal, couldn't open it to re-send a price drop.
// The backend sweep that heals this only fires after the lead has been quiet for
// 40 minutes, and every new email on the property resets that clock, so the leads
// getting the most activity are the ones that stay wedged longest.
//
// status can get stranded the same way, by a different path: a lead that gets
// filtered before underwriting runs (e.g. insufficient_credits) never has status
// flipped off 'processing', and a later resubmission that merges into the SAME
// existing lead doc and fully underwrites it doesn't touch status either —
// addEntryToLeadBatched merges price/images/sender, not status. The row is then
// stuck "working" forever despite a completed, correct underwrite.
//
// So don't trust either liveness flag over the OUTCOME. If underwriting has
// settled, the analysis is done and the lead is safe to open — whatever a stale
// status or stage says. This makes the UI self-healing: a stranded flag can no
// longer block a lead, and no data migration is needed to unwedge the ones already
// stuck. Leads that are genuinely mid-flight are still gated, by the two signals
// that actually mean it: queued/processing (checked only once settled-underwriting
// has already said no), and the pending_arv window (which keeps a stage set and is
// NOT in SETTLED_UNDERWRITING).
//
// Single source of truth for gating "can this lead be opened" across the desktop
// table rows (LeadsTable) and the map-rail / mobile cards (Leads handleLeadClick).
export function isLeadWorking(lead) {
    if (!lead) return false;
    // Checked BEFORE the settled-underwriting short-circuit, and deliberately so:
    // phase 2 is precisely the window in which underwriting reads settled and the
    // deal score is still missing, so ordering this after that return would make it
    // dead code on every lead it is meant to catch.
    if (isCoLivingScorePending(lead)) return true;
    // Outcome wins over a stale liveness flag — see comment above.
    if (SETTLED_UNDERWRITING.includes(lead.underwriting?.status)) return false;
    if (lead.status === 'queued' || lead.status === 'processing') return true;
    return lead.processingStage === 'running_underwriting'
        || lead.processingStage === 'finalizing';
}

/**
 * True while a lead's ARV + repair-cost pass is GENUINELY still in flight.
 *
 * The naive test — `status === 'queued' || status === 'processing'` — is wrong for
 * the same reason isLeadWorking() above can't use it: `status` is a liveness flag
 * and liveness flags get stranded. The worker flips a lead to 'processing' when its
 * enrichment task starts, and only the NEW-lead path writes a terminal status back
 * (saveLeadBatched sets 'complete' inside the creation batch). Every path that
 * MERGES a resubmission into an existing lead writes entries/price/images and never
 * touches status — so any property emailed in twice keeps `status: 'processing'`
 * forever, long after its analysis finished.
 *
 * Reading that flag raw pins the ARV and Repairs spinners on the lead-detail page
 * permanently: tab labels never show their values and both tab bodies render
 * "Calculating…" over a lead that has a real ARV and a real repair number sitting
 * on the doc. So trust the OUTCOME first, exactly as isLeadWorking() does — which
 * also makes this self-healing over the leads already stranded in Firestore, with
 * no backfill.
 *
 * `pending_arv` is checked FIRST and is deliberately absent from
 * SETTLED_UNDERWRITING: it is the async-split window where phase 2 still legitimately
 * owes an ARV, and it is the one case where a spinner is the honest answer.
 *
 * Takes the lead's DATA object (the shape resolveArvStatus/resolveRepairsStatus read),
 * not the Firestore wrapper.
 */
export function isUnderwritingPending(data) {
    if (!data) return false;
    const uwStatus = data.underwriting?.status;
    if (uwStatus === 'pending_arv') return true;
    // Outcome wins over a stale liveness flag.
    if (SETTLED_UNDERWRITING.includes(uwStatus)) return false;
    return data.status === 'queued' || data.status === 'processing';
}

// ---------------------------------------------------------------------------
// Per-calc resolvers
// ---------------------------------------------------------------------------

export function resolveArvStatus(data, isPending) {
    const uw = data.underwriting || {};
    if (isPending) {
        return { state: CALC_STATES.PENDING, label: 'ARV', reason: 'Underwriting in progress' };
    }
    if (isNumber(uw.arv) && uw.arv > 0) {
        return { state: CALC_STATES.OK, label: 'ARV', reason: 'Calculated' };
    }
    // Factual storyboard record wins over the heuristics below (V2 leads).
    const step = stepUnderwriting(data);
    if (step) {
        const blocked = stepBlockReason(step);
        if (blocked) return { ...blocked, label: 'ARV' };
        // ARV is computed asynchronously after the lead is created, and repair
        // cost lands first. While the step is still running we have a null ARV
        // alongside a populated repairCost — without this guard that falls
        // through to the "no comparable sales came back" failure copy below,
        // which is a false alarm. Treat an in-flight step as pending.
        if (stepStillRunning(step) && !(isNumber(uw.arv) && uw.arv > 0)) {
            return {
                state: CALC_STATES.PENDING,
                label: 'ARV',
                reason: 'ARV is still being calculated — comps analysis runs after the lead is created and can take a few minutes.'
            };
        }
        if (step.status === 'complete' && !(isNumber(step.arv) && step.arv > 0)) {
            return {
                state: CALC_STATES.SKIPPED,
                label: 'ARV',
                reason: 'ARV came back empty — no comparable sales were found for this property.'
            };
        }
    }
    if (uw.status === 'failed') {
        return { state: CALC_STATES.SKIPPED, label: 'ARV', reason: 'Underwriting failed' };
    }
    // Pipeline persisted an eligibility reason — use that verbatim.
    if (uw.reason && UW_REASON_LABELS[uw.reason]) {
        return {
            state: CALC_STATES.SKIPPED,
            label: 'ARV',
            reason: describeWithDetail(UW_REASON_LABELS[uw.reason], uw.details)
        };
    }
    // Persisted ARV-specific skip reason (written by future ingests).
    if (uw.arvSkipReason && ARV_REASON_LABELS[uw.arvSkipReason]) {
        return {
            state: CALC_STATES.SKIPPED,
            label: 'ARV',
            reason: ARV_REASON_LABELS[uw.arvSkipReason]
        };
    }
    // Pipeline didn't persist a reason — infer from lead fields.
    const inferred = inferHardBlockReason(data);
    if (inferred) {
        return { state: CALC_STATES.SKIPPED, label: 'ARV', reason: inferred };
    }
    // If repair cost succeeded, eligibility clearly wasn't the blocker.
    // The ARV lookup itself returned without a number; the sub-cause isn't
    // persisted on the lead today.
    const repairRan = isNumber(data.underwriting?.repairCost)
        && data.underwriting.repairCost >= 0;
    if (repairRan) {
        return {
            state: CALC_STATES.SKIPPED,
            label: 'ARV',
            reason: 'ARV lookup didn\'t return data — eligibility passed (repair cost ran) but no comparable sales came back. Exact reason isn\'t recorded on this lead.'
        };
    }
    return {
        state: CALC_STATES.SKIPPED,
        label: 'ARV',
        reason: 'ARV calculation did not complete — reason not recorded on the lead'
    };
}

export function resolveRepairsStatus(data, isPending) {
    const uw = data.underwriting || {};
    if (isPending) {
        return { state: CALC_STATES.PENDING, label: 'Repairs', reason: 'Underwriting in progress' };
    }
    if (isNumber(uw.repairCost) && uw.repairCost >= 0) {
        // Pipeline falls back to a sqft-based heuristic when no usable
        // images are available — the number is real but it's a rough
        // estimate, not a photo-derived assessment. Surface that distinction
        // so users know to trust photo-based estimates more.
        if (uw.repairCostEstimated === true) {
            return {
                state: CALC_STATES.ESTIMATED,
                label: 'Repairs',
                reason: 'Estimated from square footage. No usable photos were available, so the pipeline applied a per-sqft heuristic ($15/$25/$40 per sqft depending on remodel level) instead of analyzing actual photos.'
            };
        }
        return { state: CALC_STATES.OK, label: 'Repairs', reason: 'Calculated from photos' };
    }
    // Factual storyboard record wins over the heuristics below (V2 leads).
    const step = stepUnderwriting(data);
    if (step) {
        const blocked = stepBlockReason(step);
        if (blocked) return { ...blocked, label: 'Repairs' };
        if (stepStillRunning(step) && !(isNumber(uw.repairCost) && uw.repairCost >= 0)) {
            return {
                state: CALC_STATES.PENDING,
                label: 'Repairs',
                reason: 'Repair cost is still being calculated.'
            };
        }
        if (step.status === 'complete' && !(isNumber(step.repairCost) && step.repairCost >= 0)) {
            return {
                state: CALC_STATES.SKIPPED,
                label: 'Repairs',
                reason: 'Repair cost came back empty even though underwriting ran.'
            };
        }
    }
    if (uw.status === 'failed') {
        return { state: CALC_STATES.SKIPPED, label: 'Repairs', reason: 'Underwriting failed' };
    }
    // Persisted repair-specific skip reason (written by future ingests) — checked BEFORE the
    // generic eligibility reason below. By the time we're this deep into resolveRepairsStatus,
    // repairs definitely produced no number, so a present repairSkipReason is always a more
    // specific answer than the deal-level UW_REASON_LABELS entry (e.g. 'invalid_property_type'
    // → generic "Property type not supported" would otherwise always win over the more precise
    // 'vacant_land' / 'repairs_disabled' / etc., since both can be set at once for a non-SFR lead).
    if (uw.repairSkipReason && REPAIR_REASON_LABELS[uw.repairSkipReason]) {
        return {
            state: CALC_STATES.SKIPPED,
            label: 'Repairs',
            reason: REPAIR_REASON_LABELS[uw.repairSkipReason]
        };
    }
    if (uw.reason && UW_REASON_LABELS[uw.reason]) {
        return {
            state: CALC_STATES.SKIPPED,
            label: 'Repairs',
            reason: describeWithDetail(UW_REASON_LABELS[uw.reason], uw.details)
        };
    }
    // Only use eligibility inference when BOTH outputs failed — that's the
    // signature of a hard-block at the gate. If ARV succeeded but repairs
    // didn't, eligibility clearly passed; the issue is repair-specific.
    const arvSucceeded = isNumber(uw.arv) && uw.arv > 0;
    if (!arvSucceeded) {
        const inferred = inferHardBlockReason(data);
        if (inferred) {
            return { state: CALC_STATES.SKIPPED, label: 'Repairs', reason: inferred };
        }
    }
    // Best-effort inference from what's on the lead doc. Repair cost needs
    // images (or falls back to a per-sqft heuristic). If we can see there
    // are no usable images anywhere, that's the most likely cause.
    const imageReason = inferRepairImageProblem(data);
    if (imageReason) {
        return { state: CALC_STATES.SKIPPED, label: 'Repairs', reason: imageReason };
    }
    // Truly out of clues — the real cause (tier/level/band lookup failure,
    // vision API error, etc.) lives only in console logs for this lead.
    return {
        state: CALC_STATES.SKIPPED,
        label: 'Repairs',
        reason: 'Repair cost calculation did not complete (reason not captured on this lead — see Cloud Functions logs for propertyId at processing time)'
    };
}

export function resolveProfitStatus(data, isPending) {
    const uw = data.underwriting || {};
    if (isPending) {
        return { state: CALC_STATES.PENDING, label: 'Profit', reason: 'Underwriting in progress' };
    }
    if (isNumber(uw.estimatedProfit)) {
        return { state: CALC_STATES.OK, label: 'Profit', reason: 'Calculated' };
    }
    // A skipped/failed underwriting step blocks profit at the root — surface
    // that instead of the downstream "missing ARV + repairs" symptom.
    const blocked = stepBlockReason(stepUnderwriting(data));
    if (blocked) return { ...blocked, label: 'Profit' };
    const missing = [];
    if (!isNumber(uw.arv) || uw.arv <= 0) missing.push('ARV');
    if (!isNumber(uw.repairCost) || uw.repairCost < 0) missing.push('repair cost');
    if (!isNumber(data.currentAskingPrice) || data.currentAskingPrice <= 0) missing.push('asking price');
    const reason = missing.length > 0
        ? `Missing ${missing.join(' + ')}`
        : 'Not calculated';
    return { state: CALC_STATES.SKIPPED, label: 'Profit', reason };
}

/**
 * Resolve { arv, repairs, profit } statuses for a single lead.
 * Each is shaped { state, label, reason }.
 */
export function resolveCalcStatuses(lead) {
    const data = lead?.data || {};
    // 'pending_arv' is the async-split window: the lead is top-level 'complete' (enrichment
    // done) but ARV is still being computed in the finalization worker (phase 2). Treated as
    // pending so ARV/profit render a spinner instead of the false "came back empty" failure —
    // while a settled underwrite under a stranded 'processing' status is NOT. See
    // isUnderwritingPending.
    const isPending = isUnderwritingPending(data);

    return {
        arv:     resolveArvStatus(data, isPending),
        repairs: resolveRepairsStatus(data, isPending),
        profit:  resolveProfitStatus(data, isPending)
    };
}
